import { useRef, useState, useEffect } from "react";
import {
	PiCameraLight,
	PiCameraSlashLight,
	PiCameraRotateThin,
} from "react-icons/pi";
import { Camera } from "react-camera-pro";
import { base64ToFile } from "utils/helpers";
import { AiFillCheckSquare } from "react-icons/ai";
import { RiRefreshFill } from "react-icons/ri";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
} from "@mui/material";

const FileUpload = ({ onConfirm, initialUrl }) => {
	const webcamRef = useRef(null);
	const cameraRef = useRef(null);
	const [capturedImage, setCapturedImage] = useState(null);
	const [facingMode, setFacingMode] = useState("environment");
	const [cameraActive, setCameraActive] = useState(false);
	const [imageFile, setImageFile] = useState(null);
	const [open, setOpen] = useState(false);

	const newCapture = () => {
		const imageSrc = cameraRef.current.takePhoto();
		setCapturedImage(imageSrc);
		setCameraActive(false);

		if (imageSrc) {
			const timeStampp = new Date().toISOString().replace(/[:.-]/g, "");
			let filename = `$fileName_${timeStampp}.jpg`;
			if (initialUrl && initialUrl.trim() !== "") {
				const fullFileName = initialUrl.split("/").pop();
				filename = fullFileName?.split(".")[0];
			}
			const file = base64ToFile(imageSrc, filename);
			console.log("Captured File:", file);
			setImageFile(file);
		}
	};

	const confirmCapture = () => {
		onConfirm(imageFile);
		handleClose();
	};

	const toggleCamera = () => {
		setCameraActive(!cameraActive);
	};

	const toggleCameraMode = () => {
		setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
		setCameraActive(false);

		setTimeout(() => {
			setCameraActive(true);
		}, 1000);
	};

	const reset = () => {
		setCapturedImage(null);
		setCameraActive(false);
		setTimeout(() => {
			setCameraActive(true);
		}, 1000);
	};

	const stopCamera = () => {
		if (cameraActive && webcamRef.current?.video?.srcObject) {
			const stream = webcamRef.current.video.srcObject;
			const tracks = stream.getTracks();
			tracks.forEach((track) => track.stop());
		}
		setCameraActive(false);
	};

	const handleClose = () => {
		setOpen(false);
	};

	useEffect(() => {
		if (!cameraActive) {
			stopCamera();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cameraActive]);

	return (
		<div className="flex flex-col items-start gap-6 p-6">
			{/* Webcam preview or captured image */}
			<p className="text-center w-full">Take a picture directly</p>

			{!capturedImage && cameraActive && (
				<>
					<Camera
						ref={cameraRef}
						errorMessages={{ switchCamera: "" }}
						aspectRatio={7 / 6}
						facingMode={facingMode}
					/>
					<p className="font-light text-sm text-center w-full">
						click the circle button below to capture
					</p>

					<div className="flex items-center justify-between w-full">
						<button
							onClick={toggleCameraMode}
							type="button"
							className={`${
								cameraActive ? "block" : "hidden"
							} rounded-md shadow`}
						>
							<PiCameraRotateThin
								size={32}
								title="switch camera"
							/>
						</button>
						<button
							onClick={newCapture}
							type="button"
							className="bg-linear-to-r bg-primary to-primary-light text-black rounded-full w-[50px] h-[50px] shadow-primary-thin shadow-sm"
						>
							.
						</button>

						<button
							onClick={toggleCamera}
							type="button"
							className="rounded-md shadow cursor-pointer"
						>
							<PiCameraSlashLight
								size={32}
								title="turn off camera"
							/>
						</button>
					</div>
				</>
			)}

			{!capturedImage && initialUrl === "" && (
				<>
					{!cameraActive && (
						<button
							onClick={toggleCamera}
							type="button"
							className="rounded-md shadow mx-auto cursor-pointer"
						>
							<PiCameraLight size={32} title="turn on camera" />
						</button>
					)}
				</>
			)}

			{(capturedImage || initialUrl !== "") && !cameraActive && (
				<>
					<img
						src={capturedImage || initialUrl}
						alt="Captured"
						className="w-full h-auto rounded-md border border-gray-300 shadow-lg"
					/>
					{initialUrl === "" && (
						<div className="flex flex-wrap items-start justify-start w-full gap-5">
							{
								<button
									onClick={reset}
									type="button"
									className="text-gray-800 px-4 py-2 rounded-md shadow flex items-center gap-2"
								>
									<RiRefreshFill color="#0B5394" size={24} />{" "}
									Retake
								</button>
							}
							<button
								onClick={() => setOpen(true)}
								type="button"
								className="bg-transparent text-[#003300] px-4 py-2 rounded-md shadow flex items-center gap-2"
							>
								<AiFillCheckSquare color="#003300" size={24} />{" "}
								confirm
							</button>
						</div>
					)}
				</>
			)}

			<Dialog onClose={handleClose} open={open}>
				<DialogTitle>Confirm Picture</DialogTitle>

				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						are you sure you want to save this picture?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<button
						onClick={confirmCapture}
						className="mr-2 text-[#003300]"
					>
						Yes, save it
					</button>
					<button onClick={handleClose} className="text-primary mr-2">
						No
					</button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default FileUpload;
